<template>
  <div class="flex flex-col mt-16 font-sans-serif bg-white h-auto w-full px-10">
    <form @submit.prevent="saveData">
      <div class="border-b-2 border-orange-academy-150 pb-2 flex justify-between
      font-medium items-center">
        <h1 class="text-lg">{{$t('skills.personal-information')}}</h1>
        <button class="w-auto bg-orange-academy-300 text-white px-4 py-1 rounded border
          hover:bg-white hover:text-orange-academy-300 border-orange-academy-300"
          v-if="showSaveButton" >
            {{$t('stripe.save')}}
        </button>
      </div>
      <div class="flex mt-10 w-full justify-between items-center">
        <material-input class="w-full pr-4" v-model="editedTeacher.firstname"
        @input="onInputData('firstname')" :name="$t('skills.firstname')" :is-required="true" />
        <material-input class="w-full" v-model="editedTeacher.lastname"
        @input="onInputData('lastname')" :name="$t('skills.lastname')" :is-required="true" />
      </div>
      <div class="flex mt-10 w-full justify-between items-center">
        <material-input class="w-full" v-model="editedTeacher.address"
        @input="onInputData('address')" :name="$t('skills.address')" :is-required="true" />
      </div>
      <div class="flex mt-10 w-full justify-between items-center">
        <material-input class="w-full pr-4" v-model="editedTeacher.city"
        @input="onInputData('city')" :name="$t('skills.city')" :is-required="true" />
        <material-input class="w-full" v-model="editedTeacher.postcode"
        @input="onInputData('postcode')" :name="$t('skills.postcode')" :is-required="true" />
        <span class="text-red-500"
          v-if="errors.has('postcode')"
          v-text="errors.get('postcode')"
        >
        </span>
      </div>
      <div class="flex mt-10 w-full justify-between items-center">
        <material-input class="w-full pr-4" v-model="editedTeacher.state"
        @input="onInputData('state')" :name="$t('skills.state')" />
        <material-input class="w-full" v-model="editedTeacher.country"
        @input="onInputData('country')" :name="$t('skills.country')" :is-required="true" />
        <span class="text-red-500"
          v-if="errors.has('country')"
          v-text="errors.get('country')"
        >
        </span>
      </div>
      <div class="flex mt-10 w-full justify-between items-end">
        <material-input :name="$t('skills.phone')" class="w-full pr-4"
          v-model="editedTeacher.phone" @input="onInputData('phone')" />
        <material-input class="w-full" v-model="editedTeacher.age"
        @input="onInputData('age')" :name="$t('skills.age')" />
        <span class="text-red-500"
          v-if="errors.has('age')"
          v-text="errors.get('age')"
        >
        </span>
      </div>
      <div class="flex mt-10 w-full justify-between items-end">
        <material-input :name="$t('skills.professionalTitle')" class="w-full"
          v-model="editedTeacher.title" @input="onInputData('title')" />
      </div>
    </form>
    <div class="flex flex-col mt-16">
      <div class="border-b-2 border-orange-academy-150 pb-2 text-lg flex justify-start
      font-medium">
        {{$t('skills.work-experience')}}
      </div>
      <div class="flex flex-col items-start mt-6 w-full">
        <work-experience-row
          v-for="experience in workExperiences"
          :key="experience.id"
          :workExperience="experience"
          @updated="somethingHasBeenUpdated" />
        <add-new-experience v-model="addExperience" @experience-added="somethingHasBeenUpdated" />
      </div>
    </div>
    <div class="flex flex-col mt-16">
      <div class="border-b-2 border-orange-academy-150 pb-2 text-lg flex justify-start
      font-medium">
        {{$t('skills.studies')}}
      </div>
      <div class="flex flex-col items-start mt-6 w-full">
        <school-row
          v-for="school in schools"
          :key="school.id"
          :school="school"
          @updated="somethingHasBeenUpdated" />
        <add-new-school v-model="addSchool" @school-added="somethingHasBeenUpdated" />
      </div>
    </div>
    <div class="flex flex-col mt-16">
      <div class="border-b-2 border-orange-academy-150 pb-2 text-lg flex justify-start
        font-medium">
          {{$t('skills.teach')}}
      </div>
      <div class="flex flex-col items-start mt-6 w-full">
        <instrument-row
          v-for="instrument in instruments"
          :key="instrument.id"
          :instrument="instrument"
          @updated="somethingHasBeenUpdated" />
          <add-new-instrument v-model="addInstrument"
            @instrument-added="somethingHasBeenUpdated"
            :instruments="instruments"
          />
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import Errors from '@/util/Errors';
import AlgoliaMixin from '@/mixins/AlgoliaMixin';
import StreamFunctionsMixin from '@/mixins/StreamFunctionsMixin';
import { updateTeacherData, getTeacherExperiences, getTeacherInstruments } from '@/services/TeacherService';
import { getStudentSchools } from '@/services/StudentService';
import MaterialInput from '../graphics/MaterialInput.vue';
import SchoolRow from '../SchoolRow.vue';
import InstrumentRow from '../InstrumentRow.vue';
import AddNewExperience from '../AddNewExperience.vue';
import WorkExperienceRow from '../WorkExperienceRow.vue';
import AddNewSchool from '../AddNewSchool.vue';
import AddNewInstrument from '../AddNewInstrument.vue';

export default {
  components: {
    MaterialInput,
    SchoolRow,
    InstrumentRow,
    WorkExperienceRow,
    AddNewSchool,
    AddNewInstrument,
    AddNewExperience,
  },

  data() {
    return {
      editedTeacher: {
        firstname: '',
        lastname: '',
        address: '',
        postcode: '',
        city: '',
        state: '',
        country: '',
        age: '',
        phone: '',
        title: '',
      },

      workExperiences: [],
      addExperience: false,

      schools: [],
      addSchool: false,

      instruments: [],
      addInstrument: false,

      showSaveButton: false,
      errors: new Errors(),
    };
  },

  mounted() {
    Object.keys(this.editedTeacher).forEach((key) => {
      this.editedTeacher[key] = this.user.teacher[key];
    });
    if (this.editedTeacher.age) {
      this.editedTeacher.age = this.editedTeacher.age.toString();
    }
  },

  computed: {
    ...mapGetters({
      user: 'auth/user',
    }),
  },

  mixins: [AlgoliaMixin, StreamFunctionsMixin],

  created() {
    this.getSchools();
    this.getInstruments();
    this.getExperiences();
  },

  methods: {
    onInputData(field) {
      this.errors.clear(field);
      this.showSaveButton = true;
    },

    getExperiences() {
      getTeacherExperiences(this.user.teacher.id).then(({ data }) => {
        this.workExperiences = data;
      });
    },

    getSchools() {
      getStudentSchools(this.user.id).then(({ data }) => {
        this.schools = data;
      });
    },

    getInstruments() {
      getTeacherInstruments(this.user.teacher.id).then(({ data }) => {
        this.instruments = data;
      });
    },

    saveData() {
      updateTeacherData(this.user.teacher.id, this.editedTeacher).then(() => {
        this.showSaveButton = false;
        this.createNotification('success', this.$t('skills.success'));
      }).catch(({ response: { data: { errors } } }) => {
        this.errors.record(errors);
      });
    },

    somethingHasBeenUpdated() {
      this.getSchools();
      this.getInstruments();
      this.getExperiences();
      this.reset();
    },

    reset() {
      this.addExperience = false;
      this.addSchool = false;
      this.addInstrument = false;

      this.emptyAlgoliaExperiences = false;
    },

  },
};
</script>
